import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  NAV_COMPANY_LOGIN_PATH,
  NAV_COMPANY_SIGNUP_PATH,
} from "@/shared/constants/navAuthPaths.const";

export type LandingNavAuthPaths = {
  loginPath: string;
  signupPath: string;
};

const DEFAULT_LANDING_NAV_AUTH_PATHS: LandingNavAuthPaths = {
  loginPath: NAV_COMPANY_LOGIN_PATH,
  signupPath: NAV_COMPANY_SIGNUP_PATH,
};

type LandingNavAuthPathsContextValue = {
  paths: LandingNavAuthPaths;
  setNavAuthPaths: (paths: LandingNavAuthPaths | null) => void;
};

const LandingNavAuthPathsContext = createContext<LandingNavAuthPathsContextValue>({
  paths: DEFAULT_LANDING_NAV_AUTH_PATHS,
  setNavAuthPaths: () => {},
});

export function LandingNavAuthPathsProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<LandingNavAuthPaths | null>(null);

  const setNavAuthPaths = useCallback((paths: LandingNavAuthPaths | null) => {
    setOverride(paths);
  }, []);

  const value = useMemo(
    () => ({
      paths: override ?? DEFAULT_LANDING_NAV_AUTH_PATHS,
      setNavAuthPaths,
    }),
    [override, setNavAuthPaths],
  );

  return (
    <LandingNavAuthPathsContext.Provider value={value}>
      {children}
    </LandingNavAuthPathsContext.Provider>
  );
}

export function useLandingNavAuthPaths(): LandingNavAuthPaths {
  return useContext(LandingNavAuthPathsContext).paths;
}

export function useSetLandingNavAuthPaths(): LandingNavAuthPathsContextValue["setNavAuthPaths"] {
  return useContext(LandingNavAuthPathsContext).setNavAuthPaths;
}
